import React, { useState, useEffect } from 'react';
import ApiService from '../Components/apiService';
import { Table, Tooltip, Popconfirm, message, Button, Card, Row, Col, Typography, Avatar } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { BASE_URL,DateFormat,DateTimeFormat } from '../Components/Constant';
import { useSelector } from 'react-redux';
import {convertDateToUserTimezone} from '../Components/Utility';
import dayjs from 'dayjs';

const { Title } = Typography;

const DashboardList = () => {
  const [dashboards, setDashboards] = useState([]);
  const [dashboardData, setDashboardData] = useState('');
  const [isCreatingDashboard, setIsCreatingDashboard] = useState(false);
  const [loading, setLoading] = useState(false);
  const security = useSelector((state) => state.data.items[0]);
  const [pageSize, setPageSize] = useState(10);

  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboards();
  }, []);

  const fetchDashboards = async () => {
    setLoading(true);
    try {
      const apiService = new ApiService(`${BASE_URL}/get_all_dashboards`, {
        'Content-Type': 'application/json',
      }, 'GET');
      const res = await apiService.makeCall(navigate);
      
      if (res && Array.isArray(res)) {
        setDashboards(res);
      } else {
        setDashboards([]);
      }
    } catch (error) {
      console.error('Error fetching dashboards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDashboard = async (dash) => {
    if(security?.system_settings?.dashboard.delete===false){
      message.error('You dont have permission to delete');
      return;
    }
    try {
      const apiService = new ApiService(`${BASE_URL}/delete_dashboard/${dash}`, {
        'Content-Type': 'application/json',
      }, 'DELETE');
      await apiService.makeCall();
      message.success('Dashboard deleted successfully!');
      fetchDashboards();
    } catch (error) {
      console.error('Error deleting dashboard:', error);
    }
  };

  const handleEditDashboard = async (recordId) => {
    if(security?.system_settings?.dashboard.edit===false){
      message.error('You dont have permission to update');
      return;
    }
    navigate(`/setup/Dashboard/${recordId}`);
  };

  const columns = [
    {
      title: 'Dashboard Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <div>{text}</div>
      ),
    },
    {
      title: 'Default',
      dataIndex: 'isDefault',
      key: 'isDefault',
      render: (isDefault) => (
        <input type="checkbox" disabled checked={isDefault} readOnly />
      ),
    },
    {
      title: 'Scope',
      dataIndex: 'defaultScope',
      key: 'defaultScope',
    },
    {
      title: 'Created By',
      dataIndex: 'CreatedBy',
      key: 'CreatedBy',
      sorter: (a, b) => a.CreatedBy.name.localeCompare(b.CreatedBy.name),
      render: (createdBy) => (
        <a
          href={`/record/User/${createdBy?._id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        >
          <Avatar
            size='small'
            style={{
              backgroundColor: '#87d068',
              marginRight: 8,
            }}
          >
            {createdBy?.name?.charAt(0)?.toUpperCase()}
          </Avatar>
          {createdBy?.name}
        </a>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      render: (text) => (
        text && dayjs(text).isValid()
          ? convertDateToUserTimezone(text, DateTimeFormat)
          : ''
      ),
    },    
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <>
          <Tooltip title="Edit">
            <EditOutlined
              onClick={() => handleEditDashboard(record?._id)}
              style={{ marginRight: 5, fontSize: '14px', cursor: 'pointer' }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure you want to delete this Dashboard?"
              onConfirm={() => handleDeleteDashboard(record?._id)}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined style={{ color: 'red', fontSize: '14px', cursor: 'pointer' }} />
            </Popconfirm>
          </Tooltip>
        </>
      ),
    },
  ];

  const handleCreateDashboard = () => {
    if(security?.system_settings?.dashboard.create===false){
      message.error('You dont have permission to create ');
      return;
    }
    setIsCreatingDashboard(true);
    setDashboardData('');
    navigate('/setup/Dashboard');
  };

  return (
    <div>
      {!isCreatingDashboard && (
        <Card>
          <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
            <Col>
              <Title level={3}>Dashboards</Title>
            </Col>
            <Col>
              <Button type="primary" onClick={handleCreateDashboard}>New Dashboard</Button>
            </Col>
          </Row>
          <Table
            columns={columns}
            dataSource={dashboards}
            rowKey="class_name" // Or any other unique identifier
            loading={loading}
            scroll={{ x: 'max-content', y: 550 }}
            pagination={{
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setPageSize(pageSize);
              },              
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </Card>
      )}
    </div>
  );
};

export default DashboardList;